import { useValue } from "@legendapp/state/react";
import { cn } from "@repo/ui/lib/utils";
import { Sidebar, useSidebar } from "@repo/ui/ui/sidebar";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { CHAT_PANEL_DEFAULT_WIDTH, chat$ } from "~/stores/chat";
import { Chat } from "./chat";
import { isChatPaneRoute } from "./chat/chat-route";

const MIN_WIDTH = CHAT_PANEL_DEFAULT_WIDTH;
/** Fraction of the viewport width the chat panel can grow to. */
const MAX_VIEWPORT_FRACTION = 0.5;

/** Computes the effective max width based on the current viewport, floored at the min. */
const computeMaxWidth = (viewportWidth: number) =>
  Math.max(MIN_WIDTH, Math.round(viewportWidth * MAX_VIEWPORT_FRACTION));

/**
 * Bridges `chat$.isOpen` (Legend State, persisted) with the sidebar context so
 * keyboard shortcuts, command palette, and programmatic toggles all sync.
 *
 * On non-chat routes the sidebar is forced closed without touching `chat$.isOpen`,
 * so the user's preference is preserved across navigation.
 */
const useSidebarBridge = () => {
  const { open, setOpen, isMobile, setOpenMobile, openMobile } =
    useSidebar("right");
  const prevSidebarOpen = useRef<boolean | undefined>(undefined);
  const prevWantsOpen = useRef<boolean | undefined>(undefined);

  const isChatRoute = useRouterState({
    select: (s) => isChatPaneRoute(s.location.pathname),
  });

  const userWantsOpen = useValue(chat$.isOpen);
  const effectiveOpen = isChatRoute && userWantsOpen;

  // Observable + route → Sidebar
  useEffect(() => {
    queueMicrotask(() => {
      if (!isMobile) {
        setOpen(effectiveOpen);
        return;
      }

      // On mobile the chat is a full-screen overlay sheet rather than a docked
      // pane, so it must never auto-open from the persisted desktop preference
      // and must not pop open on navigation. Here `chat$.isOpen` is kept in
      // lockstep with the sheet: it starts closed, is force-closed when leaving
      // a chat route, and only opens when the user explicitly toggles it.
      const firstRun = prevWantsOpen.current === undefined;
      const wantsChanged = !firstRun && prevWantsOpen.current !== userWantsOpen;
      prevWantsOpen.current = userWantsOpen;

      if (firstRun || !isChatRoute) {
        if (userWantsOpen) chat$.isOpen.set(false);
        setOpenMobile(false);
        return;
      }
      if (wantsChanged) setOpenMobile(userWantsOpen);
    });
  }, [
    effectiveOpen,
    isChatRoute,
    isMobile,
    setOpen,
    setOpenMobile,
    userWantsOpen,
  ]);

  // Sidebar → Observable (e.g. mobile Sheet dismiss).
  // Gated on isChatRoute so route-driven closes don't overwrite persisted preference.
  // Skips the first effect so the initial SidebarProvider state isn't overwritten.
  useEffect(() => {
    const sidebarOpen = isMobile ? openMobile : open;
    if (
      isChatRoute &&
      prevSidebarOpen.current !== undefined &&
      prevSidebarOpen.current !== sidebarOpen
    ) {
      chat$.isOpen.set(sidebarOpen);
    }
    prevSidebarOpen.current = sidebarOpen;
  }, [open, openMobile, isMobile, isChatRoute]);
};

/**
 * Global AI chat panel using the Sidebar component with `side="right"`.
 * Pushes content on desktop, shows as a Sheet overlay on mobile.
 * Visible only on dataset and board pages; auto-closes when navigating elsewhere.
 */
export const ChatPanel = () => {
  useSidebarBridge();
  // Two thread-route flags that flip at different points of a navigation:
  //
  // `isThreadLocation` follows `location`, which the router updates at the
  // *start* of a navigation. Used to freeze the sidebar CSS transitions early
  // so the pane snaps shut instantly instead of animating.
  //
  // `isThreadMatched` follows `matches`, which swap when the full-page
  // `/thread/$id` route mounts or unmounts its own `<Chat>`. The pane's
  // `<Chat>` is gated on this so the pane and page never render two `<Chat>`
  // instances at once (which would show the conversation twice and have two
  // `useChat` hydrations fighting over the same thread).
  const isThreadLocation = useRouterState({
    select: (s) => s.location.pathname.startsWith("/thread/"),
  });
  const isThreadMatched = useRouterState({
    select: (s) =>
      s.matches.some((m) => m.routeId === "/_app/thread/$threadId"),
  });
  const width = useValue(chat$.width);
  const [maxWidth, setMaxWidth] = useState(() =>
    typeof window === "undefined"
      ? MIN_WIDTH
      : computeMaxWidth(window.innerWidth),
  );
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Track viewport so the max stays a percentage of the screen, and clamp the
  // current width down if the user shrinks the window below the prior max.
  useEffect(() => {
    const update = () => {
      const next = computeMaxWidth(window.innerWidth);
      setMaxWidth(next);
      const current = chat$.width.get();
      if (current > next) chat$.width.set(next);
    };
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsResizing(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Subtract because dragging left grows the right-side panel.
    const next = Math.min(
      maxWidth,
      Math.max(MIN_WIDTH, drag.startWidth - (e.clientX - drag.startX)),
    );
    chat$.width.set(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsResizing(false);
  };

  return (
    <div
      className={cn(
        // Freeze the width/offcanvas CSS transitions both while resizing and on
        // the thread route. On /thread/* the pane must snap shut instantly
        // instead of sliding. The union of both flags keeps the freeze active
        // for the whole expand *and* collapse window (location flips first on
        // expand, matches clear last on collapse).
        (isResizing || isThreadLocation || isThreadMatched) &&
          "**:data-[slot=sidebar-container]:transition-none! **:data-[slot=sidebar-gap]:transition-none!",
      )}
      style={
        {
          "--sidebar-width": `${width}px`,
          "--sidebar": "var(--background)",
        } as React.CSSProperties
      }
    >
      <Sidebar
        name="right"
        side="right"
        collapsible="offcanvas"
        className="top-(--header-height) mb-2 h-[calc(100svh-var(--header-height)-(--spacing(2)))] w-[calc(var(--sidebar-width)-(--spacing(2)))] overflow-hidden rounded-xl border bg-background data-[side=right]:right-2"
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="absolute inset-y-0 left-0 z-20 hidden w-1.5 -translate-x-1/2 cursor-col-resize touch-none select-none hover:bg-border md:block"
          aria-hidden
        />
        {!isThreadMatched && <Chat />}
      </Sidebar>
    </div>
  );
};
